import React, { useState, useEffect, useRef } from 'react';
import { UserProfile } from '../types';
import { signInWithGoogle, signInWithEmail, signUpWithEmail } from '../utils/auth';
import { LogIn, Mail, UserPlus, AlertCircle, AlertTriangle } from 'lucide-react';
import { supabase, testConnection } from '../utils/supabase';
import { useNavigate } from 'react-router-dom';

// Utility function to create a user profile from session data
const createUserProfile = (userId: string, email: string): UserProfile => {
  const isAdmin = ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(email);
  
  return {
    uid: userId,
    email: email,
    targetLanguage: localStorage.getItem('targetLanguage') || 'Dutch',
    nativeLanguage: localStorage.getItem('nativeLanguage') || 'English',
    currentLevel: localStorage.getItem('currentLevel') || 'A1',
    lessonsCompleted: [],
    dateJoined: new Date(),
    isAdmin: isAdmin,
    hasSeenGuide: localStorage.getItem('hasSeenGuide') === 'true'
  };
};

interface LoginProps {
  onLogin: (user: UserProfile) => void;
}

export function Login({ onLogin }: LoginProps) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const authInitializedRef = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    // Only run once to prevent infinite loops
    if (authInitializedRef.current) {
      return;
    }
    authInitializedRef.current = true;    
    const initializeAuth = async () => {      
      // Add a timeout to prevent infinite loading
      const authTimeout = setTimeout(() => {
        if (mountedRef.current) {
          console.log('Auth initialization timed out');
          setIsLoading(false);
          setError('Authentication service is taking too long to respond. Please try again later.');
        }
      }, 10000);
      
      try {
        // Test connection to Supabase
        await testConnection();

        // Check for existing session
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          // Create user profile from session
          const user = createUserProfile(session.user.id, session.user.email || '');
          onLogin(user);
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        clearTimeout(authTimeout);
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
    
    return () => {
      mountedRef.current = false;
    };
  }, [onLogin]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Check if we're already handling a redirect
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (code) {
        console.log('OAuth code detected in Login component, skipping new flow');
        return;
      }

      // Clear any existing session to prevent conflicts
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.warn('Error during pre-login signout:', signOutError);
        // Continue anyway
      }

      // This will redirect to Google OAuth
      const result = await signInWithGoogle();
      
      // If we get here, it means the redirect didn't happen
      if (!result) {
        // Force a page reload to ensure we get the latest state
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
      
      // The loading state will remain true during the redirect
    } catch (err) {
      console.error('Google sign in error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during Google sign in');
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Clear any error or confirmation messages
      setError('');
      setConfirmationMessage('');
      
      if (isSignUp) {
        try {
          const user = await signUpWithEmail(email, password);
          if (user) {
            if (mountedRef.current) {
              setConfirmationMessage('Account created successfully! You can now sign in.');
              setIsSignUp(false); // Switch to sign in mode
              setPassword(''); // Clear password for security
            }
          }
        } catch (err) {
          console.error('Error in signUpWithEmail:', err);
          // Special handling for "User already registered" error
          if (err instanceof Error && err.message.includes('User already registered')) {
            if (mountedRef.current) {
              setError('An account with this email already exists. Please sign in instead.');
              setIsSignUp(false); // Switch to sign in mode
            }
          } else {
            const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
            if (mountedRef.current) {
              setError(errorMessage);
              
              // If it's a server error, increment retry attempt
              if (errorMessage.includes('temporarily unavailable')) {
                setRetryAttempt(prev => prev + 1);
              }
            }
          }
        }
      } else {
        try {
          const user = await signInWithEmail(email, password);
          if (user) {
            onLogin(user);
          } else {
            if (mountedRef.current) {
              setError('Failed to sign in. Please try again.');
              setIsLoading(false);
            }
          }
        } catch (err) {
          console.error('Error in signInWithEmail:', err);
          if (mountedRef.current) {
            setError(err instanceof Error 
              ? err.message 
              : 'An error occurred while logging in. Please try again.'
            );
            setIsLoading(false);
          }
        }
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-5 p-4">
          <div className="flex flex-col items-center space-y-4">
            <div className="h-12 w-12 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin"></div>
            <div className="text-indigo-600 font-medium">Connecting to server...</div>
            <div className="text-sm text-gray-500 max-w-xs text-center mt-2">
              If this takes longer than 10 seconds, please refresh the page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Welcome to Dutch Learning
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {showEmailForm ? (isSignUp ? 'Create your account' : 'Sign in to your account') : 'Sign in to start learning Dutch'}
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {error}
                </h3>
                {retryAttempt > 0 && (
                  <p className="mt-2 text-sm text-red-700">
                    Retry attempt {retryAttempt} of 3
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {confirmationMessage && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  {confirmationMessage}
                </h3>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-6">
          {showEmailForm ? (
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div>
                <label htmlFor="email" className="sr-only">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Email address"
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Password"
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <>
                      {isSignUp ? <UserPlus className="w-5 h-5 mr-2" /> : <Mail className="w-5 h-5 mr-2" />}
                      {isSignUp ? 'Create Account' : 'Sign in with Email'}
                    </>
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailForm(false);
                    setError('');
                    setRetryAttempt(0);
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  Back to social sign in options
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setError('');
                    setRetryAttempt(0);
                    setPassword('');
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Continue with Google
                  </>
                )}
              </button>

              <button
                onClick={() => setShowEmailForm(true)}
                disabled={isLoading}
                className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="w-5 h-5 mr-2" />
                Sign in with Email
              </button>
            </div>
          )}

          <div className="text-sm text-center text-gray-600">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </div>
        </div>
      </div>
    </div>
  );
}